const ASSET_VERSION = '20260423-2';

let data = {};
let fetchProductData = null;
let productData = {};

/* ===========================
   HELPERS
   =========================== */
const normalizeSku = (s) =>
  String(s || '')
    .trim()
    .toUpperCase()
    .replace(/[‐-–—]/g, '-')
    .replace(/\u00A0/g, '')
    .replace(/\s+/g, '');

function getProductType(product) {
  return String(product?.type || product?.product_type || '').trim();
}

const isHeadlightType = (t) => String(t || '').trim() === 'LED Headlight';
const isWorklightType = (t) => String(t || '').trim() === 'LED Work Light';

async function loadConfiguratorModules() {
  const [dataModule, productModule] = await Promise.all([
    import(`./configurator-data.js?v=${ASSET_VERSION}`),
    import(`./product-data.js?v=${ASSET_VERSION}`)
  ]);

  data = dataModule.data || {};
  fetchProductData = productModule.fetchProductData;
  productData = productModule.productData || {};

  console.log('✅ Dynamic modules loaded', {
    assetVersion: ASSET_VERSION,
    productDataCount: Object.keys(productData).length,
    hasCR1059: !!productData['CR-1059'],
    hasCR3035S: !!productData['CR-3035S'],
    hasCR3036S: !!productData['CR-3036S']
  });
}

function buildProductDataByNorm() {
  const lookup = {};
  for (const [sku, obj] of Object.entries(productData || {})) {
    lookup[normalizeSku(sku)] = obj;
  }
  return lookup;
}

function money(price, centPrice) {
  if (price == null) return '';

  let p = String(price).trim();
  p = p.replace(/[£\s]/g, '');

  if (p.includes('.') || p.includes(',')) {
    p = p.replace(',', '.');
    return `£${p}`;
  }

  const c = centPrice == null ? '' : String(centPrice).trim();
  if (c && c !== '00') return `£${p}.${c.padStart(2, '0')}`;
  return `£${p}.00`;
}

function priceToNumber(price, centPrice) {
  if (price == null) return 0;

  let s = String(price).trim();
  s = s.replace(/£/g, '').replace(/[^0-9.,-]/g, '');

  if (s.includes('.') || s.includes(',')) {
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    const decPos = Math.max(lastDot, lastComma);

    let intPart = s.slice(0, decPos).replace(/[.,]/g, '');
    let decPart = s.slice(decPos + 1).replace(/[.,]/g, '');

    if (!decPart) decPart = '00';
    const n = Number(`${intPart}.${decPart}`);
    return Number.isFinite(n) ? n : 0;
  }

  const p = s.replace(/[^\d-]/g, '');
  const c = centPrice == null ? '00' : String(centPrice).trim().replace(/[^\d]/g, '');
  const n = Number(`${p}.${(c || '00').padStart(2, '0')}`);
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '£0.00';
  return `£${x.toFixed(2)}`;
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function base64UrlEncode(str) {
  return btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function base64UrlDecode(str) {
  const s = String(str).replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : '';
  return decodeURIComponent(escape(atob(s + pad)));
}

function buildResultsPayload({ brand, model, year, headTotals, workTotals, allTotals }) {
  return {
    v: 1,
    brand,
    model,
    year,
    head: headTotals?.skuTotals || {},
    work: workTotals?.skuTotals || {},
    all: allTotals?.skuTotals || {},
    ts: Date.now()
  };
}

function buildResultsLink(payload) {
  const encoded = base64UrlEncode(JSON.stringify(payload));
  return `${window.location.origin}${window.location.pathname}?kit=${encoded}`;
}

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
}

/* ===========================
   DOM HELPERS
   =========================== */
function byId(id) {
  return document.getElementById(id);
}

function show(el) {
  if (el) el.classList.remove('hidden');
}

function hide(el) {
  if (el) el.classList.add('hidden');
}

function setMessage(text) {
  const box = byId('message-container');
  if (!box) return;

  if (!text) {
    box.innerHTML = '';
    return;
  }

  box.innerHTML = `<div class="tractor-guide__message-box">${escapeHtml(text)}</div>`;
}

function clearSectionEl(id) {
  const el = byId(id);
  if (el) el.innerHTML = '';
}

function clearResultsSections() {
  clearSectionEl('headlights-section');
  clearSectionEl('worklights-section');
  clearSectionEl('accessories-section');
  clearSectionEl('lighting-package-section');
  clearSectionEl('tractorLegend');
  clearSectionEl('kitSummary');
  setMessage('');
  const bulkStatus = byId('bulkAddStatus');
  if (bulkStatus) bulkStatus.textContent = '';
}

function hideResultsUI() {
  hide(byId('results'));
  hide(byId('selected-tractor-bar'));
  hide(byId('tractorMapCard'));
}

function resetTabs() {
  document.querySelectorAll('.tractor-guide__tab').forEach((tab) => {
    tab.classList.remove('is-active');
  });

  const firstTab = document.querySelector('.tractor-guide__tab[data-target="all-results"]');
  if (firstTab) firstTab.classList.add('is-active');

  ['headlights-section', 'worklights-section', 'accessories-section', 'lighting-package-section'].forEach((id) => {
    const el = byId(id);
    if (el) el.classList.remove('hidden');
  });
}

function setSelectedTractor(brand, model, year) {
  const bar = byId('selected-tractor-bar');
  const text = byId('selected-tractor-text');

  if (!brand || !model || !year) {
    hide(bar);
    if (text) text.textContent = '';
    return;
  }

  if (text) text.textContent = `${brand} ${model} (${year})`;
  show(bar);
}

/* ===========================
   PRODUCT CARD RENDERING
   =========================== */
function ensureSectionHeader(containerId, headerText) {
  const container = byId(containerId);
  if (!container) return null;

  if (!container.querySelector('.tractor-guide__product-heading')) {
    container.insertAdjacentHTML(
      'beforeend',
      `
        <h2 class="tractor-guide__product-heading">${escapeHtml(headerText)}</h2>
        <div class="tractor-guide__product-grid"></div>
      `
    );
  }

  return container.querySelector('.tractor-guide__product-grid');
}

function renderProductCard({
  containerId,
  headerText,
  url,
  imageUrl,
  productTitle,
  price,
  centPrice,
  badgeText
}) {
  const grid = ensureSectionHeader(containerId, headerText);
  if (!grid) return;

  const safeUrl = escapeHtml(url || '#');
  const safeTitle = escapeHtml(productTitle || '');
  const priceText = money(price, centPrice);

  grid.insertAdjacentHTML(
    'beforeend',
    `
      <article class="tractor-guide__product-card">
        <a class="tractor-guide__product-link" href="${safeUrl}">
          <div class="tractor-guide__product-image-wrap">
            ${
              imageUrl
                ? `<img class="tractor-guide__product-image" src="${escapeHtml(imageUrl)}" alt="${safeTitle}" loading="lazy">`
                : ''
            }
          </div>
          <div class="tractor-guide__product-body">
            <h3 class="tractor-guide__product-title">${safeTitle}</h3>
            ${priceText ? `<div class="tractor-guide__product-price">${priceText} ex VAT</div>` : ''}
            ${badgeText ? `<div class="tractor-guide__product-badge">${escapeHtml(badgeText)}</div>` : ''}
          </div>
        </a>
      </article>
    `
  );
}

/* ===========================
   BREAKDOWN RENDERING
   =========================== */
function buildBreakdownRows(ledLightsData, productDataByNorm) {
  const positions = [
    { key: 'bonnet_headlight', label: 'Bonnet headlights' },
    { key: 'auxiliary_headlights', label: 'Auxiliary headlights' },
    { key: 'bonnet_worklight', label: 'Bonnet work lights' },
    { key: 'front_roof', label: 'Front roof' },
    { key: 'back_roof', label: 'Rear roof' },
    { key: 'side_roof', label: 'Side roof' },
    { key: 'fender_back', label: 'Rear fender' },
    { key: 'indicator_post', label: 'Indicator post' }
  ];

  const byPos = {};
  positions.forEach((p) => (byPos[p.key] = []));

  for (const rawSku of Object.keys(ledLightsData || {})) {
    const sku = normalizeSku(rawSku);
    const product = productDataByNorm[sku];
    const cleanType = getProductType(product);
    if (!product || !cleanType) continue;

    const posObj = ledLightsData[rawSku] || ledLightsData[sku] || {};

    for (const p of positions) {
      const qty = Number(posObj?.[p.key] ?? 0);
      if (!qty) continue;

      byPos[p.key].push({
        sku,
        qty,
        title: product.productTitle,
        image: product.imageUrl,
        url: product.url,
        price: product.price,
        centPrice: product.centPrice,
        type: cleanType
      });
    }
  }

  const groups = { headlights: [], worklights: [], other: [] };

  for (const p of positions) {
    const items = byPos[p.key];
    if (!items.length) continue;

    const makeRow = (list) => {
      const total = list.reduce((sum, i) => sum + i.qty, 0);

      const products = list
        .map((i) => {
          const priceStr = money(i.price, i.centPrice);
          const safeTitle = escapeHtml(i.title || '');
          const safeSku = escapeHtml(i.sku);

          return `
            <div class="tractor-guide__legend-product">
              ${
                i.image
                  ? `<img class="tractor-guide__legend-product-image" src="${escapeHtml(i.image)}" alt="${safeTitle}" loading="lazy">`
                  : `<div class="tractor-guide__legend-product-image"></div>`
              }
              <div class="tractor-guide__legend-product-info">
                <div class="tractor-guide__legend-product-title">
                  ${
                    i.url
                      ? `<a href="${escapeHtml(i.url)}">${safeSku}</a>`
                      : safeSku
                  } <span class="tractor-guide__legend-count">×${i.qty}</span>
                </div>
                <div class="tractor-guide__legend-product-subtitle">${safeTitle}</div>
                ${
                  priceStr
                    ? `<div class="tractor-guide__legend-product-price">${priceStr} <small>ex VAT</small></div>`
                    : ''
                }
              </div>
            </div>
          `;
        })
        .join('');

      return `
        <div class="tractor-guide__legend-row">
          <div class="tractor-guide__legend-row-head">
            <div class="tractor-guide__legend-name">${escapeHtml(p.label)}</div>
            <div class="tractor-guide__legend-count">${total}</div>
          </div>
          <div class="tractor-guide__legend-products">${products}</div>
        </div>
      `;
    };

    const head = items.filter((i) => isHeadlightType(i.type));
    const work = items.filter((i) => isWorklightType(i.type));
    const other = items.filter((i) => !isHeadlightType(i.type) && !isWorklightType(i.type));

    if (head.length) groups.headlights.push(makeRow(head));
    if (work.length) groups.worklights.push(makeRow(work));
    if (other.length) groups.other.push(makeRow(other));
  }

  return groups;
}

function renderBreakdown(groups) {
  const legend = byId('tractorLegend');
  const card = byId('tractorMapCard');
  if (!legend || !card) return;

  const html = `
    ${groups.headlights.length ? `<div class="tractor-guide__legend-group-title">Headlights</div>${groups.headlights.join('')}` : ''}
    ${groups.worklights.length ? `<div class="tractor-guide__legend-group-title">Work lights</div>${groups.worklights.join('')}` : ''}
    ${groups.other.length ? `<div class="tractor-guide__legend-group-title">Other</div>${groups.other.join('')}` : ''}
  `;

  legend.innerHTML = html;

  if (html.trim()) {
    show(card);
  } else {
    hide(card);
  }
}

/* ===========================
   TOTALS
   =========================== */
function buildSkuTotalsFiltered(ledLightsData, productDataByNorm, typeFilterFn) {
  const skuTotals = {};
  let totalQty = 0;
  let totalValue = 0;

  for (const rawSku of Object.keys(ledLightsData || {})) {
    const sku = normalizeSku(rawSku);
    const product = productDataByNorm[sku];
    const cleanType = getProductType(product);
    if (!product || !cleanType) continue;

    if (typeFilterFn && !typeFilterFn(cleanType)) continue;

    const posObj = ledLightsData[rawSku] || ledLightsData[sku] || {};
    let qty = 0;
    for (const v of Object.values(posObj)) qty += Number(v || 0);
    if (!qty) continue;

    skuTotals[sku] = (skuTotals[sku] || 0) + qty;
    totalQty += qty;
    totalValue += priceToNumber(product.price, product.centPrice) * qty;
  }

  return { skuTotals, totalQty, totalValue };
}

function renderSummaryCards(headTotals, workTotals, allTotals) {
  const wrap = byId('kitSummary');
  if (!wrap) return;

  wrap.innerHTML = `
    <div class="tractor-guide__summary-card">
      <div class="tractor-guide__summary-title">Headlights</div>
      <div class="tractor-guide__summary-meta">${headTotals.totalQty} item(s)</div>
      <div class="tractor-guide__summary-price">${formatGBP(headTotals.totalValue)} <small>ex VAT</small></div>
    </div>

    <div class="tractor-guide__summary-card">
      <div class="tractor-guide__summary-title">Work lights</div>
      <div class="tractor-guide__summary-meta">${workTotals.totalQty} item(s)</div>
      <div class="tractor-guide__summary-price">${formatGBP(workTotals.totalValue)} <small>ex VAT</small></div>
    </div>

    <div class="tractor-guide__summary-card tractor-guide__summary-card--highlight">
      <div class="tractor-guide__summary-title">All LED lighting</div>
      <div class="tractor-guide__summary-meta">${allTotals.totalQty} item(s)</div>
      <div class="tractor-guide__summary-price">${formatGBP(allTotals.totalValue)} <small>ex VAT</small></div>
    </div>
  `;
}

/* ===========================
   CART + LINKS
   =========================== */
function getHandleFromProductUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const parts = u.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('products');
    if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
  } catch (e) {
    const parts = String(url || '').split('/').filter(Boolean);
    const idx = parts.indexOf('products');
    if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
  }
  return null;
}

const variantCacheBySku = {};

async function resolveVariantIdForSku(sku, product) {
  const normSku = normalizeSku(sku);
  if (variantCacheBySku[normSku]) return variantCacheBySku[normSku];

  if (product?.variantId) {
    variantCacheBySku[normSku] = product.variantId;
    return product.variantId;
  }

  const handle = getHandleFromProductUrl(product?.url);
  if (!handle) return null;

  const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
  if (!res.ok) return null;

  const pj = await res.json();
  const variants = pj?.variants || [];
  const match = variants.find((v) => normalizeSku(v.sku) === normSku);
  const vid = match?.id || variants?.[0]?.id || null;

  if (vid) variantCacheBySku[normSku] = vid;
  return vid;
}

function setButtonsDisabled(disabled) {
  [
    'bulkAddHeadBtn',
    'bulkAddWorkBtn',
    'bulkAddAllBtn',
    'copyHeadLinkBtn',
    'copyWorkLinkBtn',
    'copyAllLinkBtn',
    'copyResultsPageBtn'
  ].forEach((id) => {
    const el = byId(id);
    if (el) el.disabled = disabled;
  });
}

async function addSkuTotalsToCart(skuTotals, productDataByNorm, labelForStatus = 'items') {
  const statusEl = byId('bulkAddStatus');
  if (statusEl) statusEl.textContent = `Adding ${labelForStatus}…`;

  setButtonsDisabled(true);

  try {
    const items = [];
    const missing = [];

    for (const [sku, qty] of Object.entries(skuTotals || {})) {
      const product = productDataByNorm[sku];
      if (!product) {
        missing.push(sku);
        continue;
      }

      const variantId = await resolveVariantIdForSku(sku, product);
      if (!variantId) {
        missing.push(sku);
        continue;
      }

      items.push({ id: variantId, quantity: qty });
    }

    if (!items.length) {
      if (statusEl) statusEl.textContent = 'Nothing to add.';
      return;
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Cart add failed');
    }

    if (missing.length && statusEl) {
      statusEl.textContent = `Added ✅ Missing: ${missing.slice(0, 8).join(', ')}${missing.length > 8 ? '…' : ''}`;
    } else if (statusEl) {
      statusEl.textContent = 'Added to cart ✅';
    }

    window.location.href = '/cart';
  } catch (e) {
    console.error(e);
    if (statusEl) statusEl.textContent = 'Sorry — could not add everything.';
  } finally {
    setButtonsDisabled(false);
  }
}

async function buildPrefilledCartLink(skuTotals, productDataByNorm) {
  const parts = [];
  const missing = [];

  for (const [sku, qty] of Object.entries(skuTotals || {})) {
    const product = productDataByNorm[sku];
    if (!product) {
      missing.push(sku);
      continue;
    }

    const variantId = await resolveVariantIdForSku(sku, product);
    if (!variantId) {
      missing.push(sku);
      continue;
    }

    parts.push(`${variantId}:${qty}`);
  }

  return {
    url: `${window.location.origin}/cart/${parts.join(',')}`,
    missing
  };
}

async function copyKitLink(skuTotals, productDataByNorm, label) {
  const statusEl = byId('bulkAddStatus');
  if (statusEl) statusEl.textContent = `Building link for ${label}…`;

  setButtonsDisabled(true);

  try {
    const { url, missing } = await buildPrefilledCartLink(skuTotals, productDataByNorm);
    const ok = await copyTextToClipboard(url);

    if (ok) {
      if (statusEl) {
        statusEl.textContent = `Copied link ✅${missing.length ? ` Missing: ${missing.slice(0, 6).join(', ')}${missing.length > 6 ? '…' : ''}` : ''}`;
      }
    } else {
      prompt('Copy this link:', url);
      if (statusEl) statusEl.textContent = 'Link ready.';
    }
  } catch (e) {
    console.error(e);
    if (statusEl) statusEl.textContent = 'Could not build link.';
  } finally {
    setButtonsDisabled(false);
  }
}

async function copyResultsPageLink(payload) {
  const statusEl = byId('bulkAddStatus');
  if (statusEl) statusEl.textContent = 'Building results page link…';

  const url = buildResultsLink(payload);
  const ok = await copyTextToClipboard(url);

  if (ok) {
    if (statusEl) statusEl.textContent = 'Results page link copied ✅';
  } else {
    prompt('Copy this link:', url);
    if (statusEl) statusEl.textContent = 'Results page link ready.';
  }
}

/* ===========================
   LEAD EMAIL
   =========================== */
function buildLeadEmailBodySync(state) {
  const lines = [];
  lines.push('LED Configurator Enquiry');
  lines.push('------------------------');
  lines.push(`Tractor: ${state.brand} / ${state.model} / ${state.year}`);
  lines.push('');
  lines.push(`Headlights: ${state.headTotals.totalQty} item(s) — ${formatGBP(state.headTotals.totalValue)} ex VAT`);
  lines.push(`Work lights: ${state.workTotals.totalQty} item(s) — ${formatGBP(state.workTotals.totalValue)} ex VAT`);
  lines.push(`All LED lighting: ${state.allTotals.totalQty} item(s) — ${formatGBP(state.allTotals.totalValue)} ex VAT`);
  lines.push('');
  lines.push(`Results page link: ${state.resultsPageLink}`);
  lines.push('');
  lines.push('SKU totals (quick):');
  lines.push(
    JSON.stringify(
      {
        headlights: state.headTotals.skuTotals || {},
        worklights: state.workTotals.skuTotals || {},
        all: state.allTotals.skuTotals || {}
      },
      null,
      2
    )
  );

  return lines.join('\n');
}

/* ===========================
   STATE
   =========================== */
let currentKitState = null;
let productsLoaded = false;

/* ===========================
   RENDER MAIN RESULTS
   =========================== */
function renderProducts(ledLightsData, productDataByNorm) {
  let worklightCount = 0;
  let headlightCount = 0;
  let accessoryCount = 0;
  let packageCount = 0;
  let skipped = 0;

  for (const rawSku of Object.keys(ledLightsData || {})) {
    const sku = normalizeSku(rawSku);
    const product = productDataByNorm[sku];
    const cleanType = getProductType(product);

    if (!product || !cleanType) {
      skipped++;
      console.log('Skipped product:', sku, {
        exists: !!product,
        product,
        type: product?.type,
        product_type: product?.product_type,
        title: product?.productTitle,
        url: product?.url
      });
      continue;
    }

    const { url, imageUrl, productTitle, price, centPrice } = product;

    switch (cleanType) {
      case 'LED Work Light':
        worklightCount++;
        renderProductCard({
          containerId: 'worklights-section',
          headerText: 'Work lights',
          url,
          imageUrl,
          productTitle,
          price,
          centPrice,
          badgeText: `Option ${worklightCount}`
        });
        break;

      case 'LED Headlight':
        headlightCount++;
        renderProductCard({
          containerId: 'headlights-section',
          headerText: 'Headlights',
          url,
          imageUrl,
          productTitle,
          price,
          centPrice,
          badgeText: `Option ${headlightCount}`
        });
        break;

      case 'Headlight Lighting Accessory':
      case 'Worklight Lighting Accessory':
        accessoryCount++;
        renderProductCard({
          containerId: 'accessories-section',
          headerText: 'Lighting accessories',
          url,
          imageUrl,
          productTitle,
          price,
          centPrice,
          badgeText: `Accessory ${accessoryCount}`
        });
        break;

      case 'LED Light Package':
      case 'LED Worklight Package':
      case 'LED Headlight Package':
        packageCount++;
        renderProductCard({
          containerId: 'lighting-package-section',
          headerText: 'Lighting packages',
          url,
          imageUrl,
          productTitle,
          price,
          centPrice,
          badgeText: `Package ${packageCount}`
        });
        break;

      default:
        skipped++;
        console.log('Skipped by type:', sku, cleanType);
        break;
    }
  }

  if (skipped > 0) {
    console.info(`ℹ️ ${skipped} products skipped (missing data/type).`);
  }

  ['headlights-section', 'worklights-section', 'accessories-section', 'lighting-package-section'].forEach((id) => {
    const el = byId(id);
    if (el && !el.innerHTML.trim()) {
      el.classList.add('hidden');
    } else if (el) {
      el.classList.remove('hidden');
    }
  });
}

/* ===========================
   INIT
   =========================== */
document.addEventListener('DOMContentLoaded', async function () {
  const brandSelect = byId('brand-select');
  const modelSelect = byId('model-select');
  const yearSelect = byId('year-select');
  const resetBtn = byId('reset-finder');

  if (!brandSelect || !modelSelect || !yearSelect) return;

  brandSelect.disabled = true;
  modelSelect.disabled = true;
  yearSelect.disabled = true;

  try {
    await loadConfiguratorModules();
    await fetchProductData(productData);
    productsLoaded = true;

    console.log('✅ productData loaded after dynamic import');
    console.log('CR-1059 direct check:', productData['CR-1059']);
    console.log('CR-3035S direct check:', productData['CR-3035S']);
    console.log('CR-3036S direct check:', productData['CR-3036S']);
  } catch (e) {
    console.error('Module/data load error', e);
  } finally {
    brandSelect.disabled = false;
  }

  hide(byId('no-results-box'));
  hideResultsUI();

  function hardReset() {
    brandSelect.selectedIndex = 0;
    modelSelect.innerHTML = '<option value="" disabled selected>Select model</option>';
    yearSelect.innerHTML = '<option value="" disabled selected>Select year</option>';

    modelSelect.disabled = true;
    yearSelect.disabled = true;

    clearResultsSections();
    hideResultsUI();
    hide(byId('no-results-box'));
    currentKitState = null;
    resetTabs();
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', hardReset);
  }

  document.querySelectorAll('.tractor-guide__popular-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!productsLoaded) return;
      brandSelect.value = btn.dataset.brand;
      brandSelect.dispatchEvent(new Event('change'));
    });
  });

  document.querySelectorAll('.tractor-guide__tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.tractor-guide__tab').forEach((t) => t.classList.remove('is-active'));
      tab.classList.add('is-active');

      const target = tab.dataset.target;
      const ids = ['headlights-section', 'worklights-section', 'accessories-section', 'lighting-package-section'];

      if (target === 'all-results') {
        ids.forEach((id) => {
          const el = byId(id);
          if (el && el.innerHTML.trim()) el.classList.remove('hidden');
        });
        return;
      }

      ids.forEach((id) => {
        const el = byId(id);
        if (!el) return;
        if (!el.innerHTML.trim()) {
          el.classList.add('hidden');
          return;
        }
        if (id === target) {
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });
    });
  });

  let leadSubmitted = false;
  const iframe = byId('ledLeadIframe');
  if (iframe) {
    iframe.addEventListener('load', () => {
      if (!leadSubmitted) return;

      const statusEl = byId('led_form_status');
      const btn = byId('led_submit_btn');

      if (statusEl) statusEl.innerHTML = 'Sent — we’ll email you shortly ✔';
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Sent ✔';
      }
    });
  }

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || form.id !== 'ledLeadForm') return;

      const hp = byId('led_hp');
      const statusEl = byId('led_form_status');
      const btn = byId('led_submit_btn');
      const bodyField = byId('led_results_payload');
      const resultsField = byId('led_results_results');

      if (hp && hp.value) {
        e.preventDefault();
        return;
      }

      if (!currentKitState || !currentKitState.allTotals?.totalQty) {
        e.preventDefault();
        if (statusEl) statusEl.textContent = 'Please select a tractor first so we have results to send.';
        return;
      }

      const message = buildLeadEmailBodySync(currentKitState);
      if (bodyField) bodyField.value = message;
      if (resultsField) resultsField.value = message;

      leadSubmitted = true;
      if (btn) btn.disabled = true;
      if (statusEl) statusEl.textContent = 'Sending…';
    },
    true
  );

  brandSelect.addEventListener('change', function () {
    if (!productsLoaded) return;

    const brand = this.value;

    modelSelect.innerHTML = '<option value="" disabled selected>Select model</option>';
    yearSelect.innerHTML = '<option value="" disabled selected>Select year</option>';
    modelSelect.disabled = true;
    yearSelect.disabled = true;

    clearResultsSections();
    hideResultsUI();
    hide(byId('no-results-box'));
    currentKitState = null;
    resetTabs();

    if (!data[brand]) return;

    Object.keys(data[brand]).forEach((model) => {
      const opt = document.createElement('option');
      opt.value = model;
      opt.textContent = model;
      modelSelect.appendChild(opt);
    });

    modelSelect.disabled = false;
  });

  modelSelect.addEventListener('change', function () {
    if (!productsLoaded) return;

    const brand = brandSelect.value;
    const model = this.value;

    yearSelect.innerHTML = '<option value="" disabled selected>Select year</option>';
    yearSelect.disabled = true;

    clearResultsSections();
    hideResultsUI();
    hide(byId('no-results-box'));
    currentKitState = null;
    resetTabs();

    if (!data[brand]?.[model]) return;

    Object.keys(data[brand][model]).forEach((year) => {
      const opt = document.createElement('option');
      opt.value = year;
      opt.textContent = year;
      yearSelect.appendChild(opt);
    });

    yearSelect.disabled = false;
  });

  yearSelect.addEventListener('change', function () {
    if (!productsLoaded) return;

    const brand = brandSelect.value;
    const model = modelSelect.value;
    const year = this.value;

    clearResultsSections();
    hide(byId('no-results-box'));
    resetTabs();

    const ledLightsData = data[brand]?.[model]?.[year];
    if (!ledLightsData) {
      hide(byId('results'));
      show(byId('no-results-box'));
      setSelectedTractor(brand, model, year);
      return;
    }

    console.log('Selected dataset:', {
      brand,
      model,
      year,
      ledLightsData
    });

    const productDataByNorm = buildProductDataByNorm();

    console.log('Lookup checks:', {
      rawCR1059: productData['CR-1059'],
      normCR1059: productDataByNorm['CR-1059'],
      rawCR3035S: productData['CR-3035S'],
      normCR3035S: productDataByNorm['CR-3035S'],
      rawCR3036S: productData['CR-3036S'],
      normCR3036S: productDataByNorm['CR-3036S']
    });

    renderProducts(ledLightsData, productDataByNorm);

    const groups = buildBreakdownRows(ledLightsData, productDataByNorm);
    renderBreakdown(groups);

    const headTotals = buildSkuTotalsFiltered(ledLightsData, productDataByNorm, isHeadlightType);
    const workTotals = buildSkuTotalsFiltered(ledLightsData, productDataByNorm, isWorklightType);
    const allTotals = buildSkuTotalsFiltered(
      ledLightsData,
      productDataByNorm,
      (t) => isHeadlightType(t) || isWorklightType(t)
    );

    renderSummaryCards(headTotals, workTotals, allTotals);

    setSelectedTractor(brand, model, year);
    show(byId('results'));
    setMessage(`Compatible LED lighting options found for ${brand} ${model} (${year}).`);

    const payload = buildResultsPayload({ brand, model, year, headTotals, workTotals, allTotals });

    currentKitState = {
      brand,
      model,
      year,
      ledLightsData,
      productDataByNorm,
      headTotals,
      workTotals,
      allTotals,
      resultsPageLink: buildResultsLink(payload)
    };

    const btnH = byId('bulkAddHeadBtn');
    const btnW = byId('bulkAddWorkBtn');
    const btnA = byId('bulkAddAllBtn');
    const copyH = byId('copyHeadLinkBtn');
    const copyW = byId('copyWorkLinkBtn');
    const copyA = byId('copyAllLinkBtn');
    const copyResultsBtn = byId('copyResultsPageBtn');

    if (btnH) btnH.onclick = () => addSkuTotalsToCart(headTotals.skuTotals, productDataByNorm, 'headlights');
    if (btnW) btnW.onclick = () => addSkuTotalsToCart(workTotals.skuTotals, productDataByNorm, 'work lights');
    if (btnA) btnA.onclick = () => addSkuTotalsToCart(allTotals.skuTotals, productDataByNorm, 'full kit');

    if (copyH) copyH.onclick = () => copyKitLink(headTotals.skuTotals, productDataByNorm, 'headlights');
    if (copyW) copyW.onclick = () => copyKitLink(workTotals.skuTotals, productDataByNorm, 'work lights');
    if (copyA) copyA.onclick = () => copyKitLink(allTotals.skuTotals, productDataByNorm, 'full kit');
    if (copyResultsBtn) copyResultsBtn.onclick = () => copyResultsPageLink(payload);
  });

  const params = new URLSearchParams(window.location.search);
  const kit = params.get('kit');

  if (kit) {
    try {
      const payload = JSON.parse(base64UrlDecode(kit));
      const b = payload?.brand;
      const m = payload?.model;
      const y = payload?.year;

      const tryApply = () => {
        if (!productsLoaded) {
          setTimeout(tryApply, 100);
          return;
        }

        if (b) {
          brandSelect.value = b;
          brandSelect.dispatchEvent(new Event('change'));
        }

        setTimeout(() => {
          if (m) {
            modelSelect.value = m;
            modelSelect.dispatchEvent(new Event('change'));
          }

          setTimeout(() => {
            if (y) {
              yearSelect.value = y;
              yearSelect.dispatchEvent(new Event('change'));
            }
          }, 80);
        }, 80);
      };

      tryApply();
    } catch (e) {
      console.warn('Invalid kit link', e);
    }
  }
});